/**
 * P3-42: ChallengePanel —— 挑战列表面板。
 *
 * 对齐 `js/challenges.js:902-950`：
 * - pending > 0 时显示 resetMessage（含 chronosphere 提示）
 * - 下方渲染全部已解锁挑战按钮
 */
import { useGameTick } from "../../bridge/useGameTick";
import { t } from "../../bridge/i18n";
import type { GamePage } from "../../bridge/GameBridge";
import { SpacePanel } from "../space/SpacePanel";
import ChallengeBtn from "./ChallengeBtn";

interface ChallengeMeta {
  name: string;
  unlocked?: boolean;
}

interface ChallengePanelSnapshot {
  resetMessage: string | null;
  challenges: ChallengeMeta[];
}

function getResetMessage(game: GamePage | null): string | null {
  if (!game) {
    return null;
  }
  const count = game.challenges?.getCountPending?.() ?? 0;
  if (count <= 0) {
    return null;
  }
  let msg = t("challendge.panel.pending", [count]);
  if (game.bld?.get?.("chronosphere")?.val > 0) {
    msg += t("challendge.btn.chronosphere.desc");
  }
  return msg;
}

function getSnapshot(game: GamePage | null): ChallengePanelSnapshot | null {
  if (!game) {
    return null;
  }
  const challenges = (game.challenges?.challenges as ChallengeMeta[] | undefined) ?? [];
  return {
    resetMessage: getResetMessage(game),
    challenges: challenges.map((ch) => ({ name: ch.name, unlocked: Boolean(ch.unlocked) })),
  };
}

function snapEq(a: ChallengePanelSnapshot | null, b: ChallengePanelSnapshot | null): boolean {
  if (a === b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }
  if (a.resetMessage !== b.resetMessage || a.challenges.length !== b.challenges.length) {
    return false;
  }
  for (let i = 0; i < a.challenges.length; i++) {
    if (
      a.challenges[i].name !== b.challenges[i].name ||
      a.challenges[i].unlocked !== b.challenges[i].unlocked
    ) {
      return false;
    }
  }
  return true;
}

export default function ChallengePanel() {
  const snapshot = useGameTick(
    (game) => getSnapshot(game as GamePage | null),
    snapEq,
  );

  if (!snapshot) {
    return null;
  }

  return (
    <SpacePanel title="challendge.panel.label">
      {snapshot.resetMessage && (
        <span style={{ display: "inline-block", marginBottom: "16px" }}>
          {snapshot.resetMessage}
        </span>
      )}
      {snapshot.challenges.map((challenge) => (
        <ChallengeBtn key={challenge.name} challenge={challenge} />
      ))}
    </SpacePanel>
  );
}
